import React, { useEffect, useState } from "react";
import { Container, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import {
    DocumentData,
    collection,
    getDocs,
    limit,
    query,
    where,
    doc,
    updateDoc,
    addDoc,
    deleteDoc,
    getDoc,
    documentId
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { toast, ToastContainer } from "react-toastify";
import { logError } from "../ErrorLogService/ErrorLoggingService";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import dayjs from "dayjs";
import { generateSlotsFromSchedule } from "../../utils/slotGeneration";
import { saveTrainerMonthlyPayout } from "../../utils/trainerPayouts";
import useBranchId from "../../hooks/useBranchId";

// Import Modular Child Components
import TrainerList from "./TrainerList";
import TrainerFormModal from "./TrainerFormModal";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../routes/routesPaths";

export default function Trainers() {
    const companyID = useAppSelector((state: RootState) => state.session?.company_id) || localStorage.getItem("company_id") || "";
    const { branchesList } = useAppSelector((state: RootState) => state.branchList);
    const { branchId: branchID, setBranchId: setBranchID } = useBranchId(branchesList);
    const [trainersData, setTrainersData] = useState<any[]>([]);
    const [tableLoader, setTableLoader] = useState(false);
    const [opened, { open, close }] = useDisclosure(false);
    const [savebtnLoader, setsavebtnLoader] = useState(false);

    const navigate = useNavigate();

    // Edit Trainer State
    const [editingTrainerId, setEditingTrainerId] = useState<string | null>(null);

    const form = useForm({
        initialValues: {
            first_name: "",
            last_name: "",
            email: "",
            branch_id: "",
            type: "trainer",
            designation: "Certified Trainer",
            wage_type: "fixed",
            wage_amount: 1000,
            status: "Active",
            mobile_number: ""
        },

        validate: (values) => {
            return {
                first_name:
                    values.first_name.trim().length < 2
                        ? "First name must include at least 2 characters"
                        : null,
                last_name:
                    values.last_name.length < 2
                        ? "Last name must include at least 2 characters"
                        : null,
                email: /^\S+@\S+$/.test(values.email) ? null : "Invalid email",
                branch_id: values.branch_id ? null : "Select branch",
                mobile_number: !values.mobile_number
                    ? "Mobile number is required"
                    : !/^[1-9]\d{9}$/.test(values.mobile_number)
                    ? "Invalid mobile number"
                    : null,
            };
        },
    });

    useEffect(() => {
        if (!companyID || !branchID) return;
        queryTrainerList(branchID);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyID, branchID]);

    async function queryTrainerList(branchIDS: any) {
        setTableLoader(true);
        try {
            let q;
            if (branchIDS === "all" || branchIDS === "all_branches") {
                q = query(
                    collection(db, "companyprofile", companyID, "trainers"),
                    limit(200)
                );
            } else {
                q = query(
                    collection(db, "companyprofile", companyID, "trainers"),
                    where("branch_id", "==", String(branchIDS)),
                    limit(100)
                );
            }

            const querySnapshot = await getDocs(q);
            const results: DocumentData[] = [];

            querySnapshot.forEach((doc) => {
                results.push({ id: doc.id, ...doc.data() });
            });
            setTrainersData(results);
            setTableLoader(false);
        } catch (error) {
            setTableLoader(false);
            console.error("Error querying documents:", error);
            logError(error, "queryTrainerList Trainer");
        }
    }

    const handleAddModalOpen = () => {
        setEditingTrainerId(null);
        form.setValues({
            first_name: "",
            last_name: "",
            email: "",
            branch_id: branchID || "",
            type: "trainer",
            designation: "Certified Trainer",
            wage_type: "fixed",
            wage_amount: 1000,
            status: "Active",
            mobile_number: ""
        });
        open();
    };

    const handleEditModalOpen = (trainer: any) => {
        setEditingTrainerId(trainer.id);
        form.setValues({
            first_name: trainer.first_name || "",
            last_name: trainer.last_name || "",
            email: trainer.email || "",
            branch_id: trainer.branch_id || branchID || "",
            type: "trainer",
            designation: trainer.designation || "Certified Trainer",
            wage_type: trainer.wage_type || "fixed",
            wage_amount: trainer.wage_amount || 1000,
            status: trainer.status || "Active",
            mobile_number: trainer.mobile_number || ""
        });
        open();
    };

    const handleSaveTrainer = async () => {
        setsavebtnLoader(true);
        if (form.validate().hasErrors) {
            setsavebtnLoader(false);
            return;
        }

        try {
            if (editingTrainerId) {
                // Edit / Update directly in Firestore
                const docRef = doc(db, "companyprofile", companyID, "trainers", editingTrainerId);
                await updateDoc(docRef, {
                    first_name: form.values.first_name,
                    last_name: form.values.last_name,
                    email: form.values.email,
                    branch_id: form.values.branch_id,
                    designation: form.values.designation,
                    wage_type: form.values.wage_type,
                    wage_amount: form.values.wage_amount,
                    status: form.values.status,
                    mobile_number: form.values.mobile_number
                });
                toast.success("Trainer updated successfully!");
                close();
                queryTrainerList(branchID);
            } else {
                // Direct Firestore check to see if email is already in use
                const adminEmailQ = query(collection(db, "companyprofile", companyID, "trainers"), where("email", "==", form.values.email));
                const adminEmailSnap = await getDocs(adminEmailQ);
                if (!adminEmailSnap.empty) {
                    toast.warn("This email is already registered as a trainer or staff member.");
                    setsavebtnLoader(false);
                    return;
                }

                // Add new trainer directly to the trainers subcollection
                await addDoc(collection(db, "companyprofile", companyID, "trainers"), {
                    first_name: form.values.first_name,
                    last_name: form.values.last_name,
                    email: form.values.email,
                    branch_id: form.values.branch_id,
                    company_id: companyID,
                    type: "trainer",
                    role: "trainer",
                    designation: form.values.designation,
                    wage_type: form.values.wage_type,
                    wage_amount: form.values.wage_amount,
                    status: form.values.status || "Active",
                    mobile_number: form.values.mobile_number
                });

                close();
                setsavebtnLoader(false);
                toast.success("Trainer created successfully!");
                queryTrainerList(branchID);
            }
        } catch (error) {
            console.error("Error saving trainer:", error);
            toast.error("Failed to save trainer.");
            setsavebtnLoader(false);
        }
    };

    // Navigate to Standalone Trainer View Route
    const handleViewDetailsPage = async (trainer: any) => {
        navigate(ROUTES.TRAINER_DETAILS.replace(":trainerId", trainer.id));
    };

    // Delete Trainer
    const handleDeleteTrainer = async (trainerId: string) => {
        modals.openConfirmModal({
            title: 'Delete Trainer',
            children: (
                <Text size="sm">
                    Are you sure you want to delete this trainer?
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, "companyprofile", companyID, "trainers", trainerId));
                    toast.success("Trainer deleted successfully");
                    queryTrainerList(branchID);
                } catch (err) {
                    console.error("Error deleting trainer:", err);
                    toast.error("Failed to delete trainer");
                }
            }
        });
    };

    return (
        <div>
            <Container size="lg" py="lg">
                <ToastContainer />

                <TrainerList
                    branchList={branchesList}
                    branchID={branchID}
                    setBranchID={setBranchID}
                    trainersData={trainersData}
                    tableLoader={tableLoader}
                    handleViewDetailsPage={handleViewDetailsPage}
                    handleEditModalOpen={handleEditModalOpen}
                    handleDeleteTrainer={handleDeleteTrainer}
                    openAddModal={handleAddModalOpen}
                    queryTrainerList={queryTrainerList}
                />

                <TrainerFormModal
                    opened={opened}
                    onClose={close}
                    editingTrainerId={editingTrainerId}
                    form={form}
                    branchList={branchesList}
                    handleSaveTrainer={handleSaveTrainer}
                    savebtnLoader={savebtnLoader}
                />
            </Container>
        </div>
    );
}